import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useRegisterChatInput } from '../../hooks/useChatInputRegistry';
import { Send, Zap, CheckCircle, Clock, Paperclip, Mic, ArrowLeft, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Eye, MessageSquare, Heart, Calendar, Repeat, Plus, BarChart3, X, Brain, Hash, AtSign, Image as ImageIcon, Film, LayoutGrid, Link2, Play } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { SOCIAL_DATA, SOCIAL_CHAT_RESPONSES } from '../../data/socialData';
import V4AdHocChat from './V4AdHocChat';
import PRIYA_SOCIAL_NEW_POST from '../../data/priyaSocialNewPostData';
import PRIYA_SOCIAL_KEYPRESS from '../../data/priyaSocialKeypressData';
import { buildSocialDeepDive } from '../../data/priyaSocialDeepDiveData';
import PRIYA_SOCIAL_CHANNEL_DD from '../../data/priyaSocialChannelDDData';

const AGENT_NAMES = { buzz: 'Buzz', wright: 'Wright', marsi: 'Marsi' };
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const TODAY = '2025-01-27';

const PLATFORM_STYLES = {
  linkedin: { label: 'LinkedIn', color: 'text-blue-400', bg: 'bg-blue-400/10', lightColor: 'text-blue-600', lightBg: 'bg-blue-600/10', dot: 'bg-blue-400' },
  twitter: { label: 'X', color: 'text-sky-400', bg: 'bg-sky-400/10', lightColor: 'text-sky-600', lightBg: 'bg-sky-600/10', dot: 'bg-sky-400' },
  instagram: { label: 'IG', color: 'text-pink-400', bg: 'bg-pink-400/10', lightColor: 'text-pink-600', lightBg: 'bg-pink-600/10', dot: 'bg-pink-400' },
  facebook: { label: 'Facebook', color: 'text-blue-500', bg: 'bg-blue-500/10', lightColor: 'text-blue-700', lightBg: 'bg-blue-700/10', dot: 'bg-blue-500' },
  tiktok: { label: 'TikTok', color: 'text-fuchsia-400', bg: 'bg-fuchsia-400/10', lightColor: 'text-fuchsia-600', lightBg: 'bg-fuchsia-600/10', dot: 'bg-fuchsia-400' },
};

const POST_STATUS = {
  published: { label: 'Published', dark: 'text-emerald-400 bg-emerald-400/10', light: 'text-emerald-600 bg-emerald-600/10' },
  scheduled: { label: 'Scheduled', dark: 'text-indigo-400 bg-indigo-400/10', light: 'text-indigo-600 bg-indigo-600/10' },
  draft: { label: 'Draft', dark: 'text-white/40 bg-white/[0.06]', light: 'text-slate-500 bg-slate-200/60' },
};

/* ── Helpers ── */
const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const baseGetPostsForDate = (dk) => SOCIAL_DATA.postsByDate[dk] || [];
const getWeekStart = (d) => { const r = new Date(d); r.setDate(r.getDate() - r.getDay() + 1); return r; };
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };

/* ── Platform Card ── */
const PlatformCard = ({ platform, isDark, onViewReport, onChannelDeepDive }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';
  const ps = PLATFORM_STYLES[platform.id];
  const engTrendUp = platform.engTrend.startsWith('+');

  return (
    <div className={`${cardBg} border ${border} rounded-xl px-3 py-2.5 flex-1 min-w-0`} data-testid={`social-platform-${platform.id}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${isDark ? ps.bg + ' ' + ps.color : ps.lightBg + ' ' + ps.lightColor}`}>{ps.label}</span>
        <span className={`text-[9px] ${ts}`}>{platform.posts} posts</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <p className={`text-[8px] ${ts}`}>Followers</p>
          <p className={`text-xs font-bold ${tp}`}>{platform.followers}</p>
          <p className={`text-[8px] ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{platform.followersGrowth}</p>
        </div>
        <div>
          <p className={`text-[8px] ${ts}`}>Impressions</p>
          <p className={`text-xs font-bold ${tp}`}>{platform.impressions}</p>
        </div>
        <div>
          <p className={`text-[8px] ${ts}`}>Engagement</p>
          <p className={`text-xs font-bold ${tp}`}>{platform.engagement}</p>
          <p className={`text-[8px] ${engTrendUp ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{platform.engTrend}</p>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <button
          onClick={() => onViewReport(platform.id)}
          className={`flex items-center gap-1 text-[9px] font-medium transition-colors ${isDark ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-500'}`}
          data-testid={`social-report-${platform.id}`}
        >
          <BarChart3 className="w-3 h-3" /> View Report
        </button>
        {onChannelDeepDive && (
          <button
            onClick={() => onChannelDeepDive(platform.id)}
            className={`flex items-center gap-1 text-[9px] font-medium transition-colors ${isDark ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-500'}`}
            data-testid={`social-channel-deepdive-${platform.id}`}
          >
            <Zap className="w-3 h-3" /> Deep Dive
          </button>
        )}
      </div>
    </div>
  );
};

/* ── Expanded Post Row ── */
const PostRow = ({ post, isDark, onApprove, onDetails, onDeepDive, approved }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const hoverBg = isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-black/[0.02]';
  const ps = PLATFORM_STYLES[post.platform];
  const status = POST_STATUS[post.status];
  const isNew = post.isNew;

  return (
    <div className={`flex items-start gap-3 px-3 py-2.5 rounded-lg border ${isNew ? (isDark ? 'border-indigo-500/25' : 'border-indigo-300/60') : border} ${isNew ? (isDark ? 'bg-indigo-950/20' : 'bg-indigo-50/60') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]')}`} data-testid={`social-post-${post.id}`}>
      <span className={`text-[11px] font-medium w-16 flex-shrink-0 pt-0.5 ${tp}`}>{post.time}</span>
      <div className="flex flex-col items-center flex-shrink-0 pt-1">
        <div className={`w-2.5 h-2.5 rounded-full ${post.status === 'published' ? 'bg-emerald-400' : post.status === 'scheduled' ? 'bg-indigo-400' : (isDark ? 'bg-white/20' : 'bg-slate-300')}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? ps.bg + ' ' + ps.color : ps.lightBg + ' ' + ps.lightColor}`}>{ps.label}</span>
          <span className={`text-[9px] ${ts}`}>{post.type}</span>
          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? status.dark : status.light}`}>{approved ? 'Approved' : status.label}</span>
        </div>
        <p className={`text-xs font-semibold ${tp}`}>{post.title}</p>
        <p className={`text-[10px] mt-0.5 leading-relaxed line-clamp-2 ${ts}`}>{post.preview}</p>
        {post.engagement && (
          <div className="flex items-center gap-3 mt-1.5">
            <span className={`flex items-center gap-1 text-[10px] ${ts}`}><Heart className="w-3 h-3" /> {post.engagement.likes}</span>
            <span className={`flex items-center gap-1 text-[10px] ${ts}`}><MessageSquare className="w-3 h-3" /> {post.engagement.comments}</span>
            <span className={`flex items-center gap-1 text-[10px] ${ts}`}><Repeat className="w-3 h-3" /> {post.engagement.shares}</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {onDeepDive && (
          <button
            onClick={() => onDeepDive(post.id)}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-indigo-500/15 text-indigo-400 hover:bg-indigo-500/25' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/20'}`}
            data-testid={`social-deepdive-${post.id}`}
          >
            <Zap className="w-3 h-3" /> Deep Dive
          </button>
        )}
        <button
          onClick={() => onDetails?.(post.id)}
          className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${border} ${ts} ${hoverBg}`}
          data-testid={`social-details-${post.id}`}
        >
          <Eye className="w-3 h-3" /> Details
        </button>
        {post.needsReview && !approved && (
          <button
            onClick={() => onApprove(post.id)}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20' : 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/15'}`}
            data-testid={`social-approve-${post.id}`}
          >
            <CheckCircle className="w-3 h-3" /> Approve
          </button>
        )}
      </div>
    </div>
  );
};

/* ── Social Details Panel (slide-in right drawer) ── */
const MEDIA_TYPE_ICONS = {
  carousel: LayoutGrid,
  reel: Film,
  thread: MessageSquare,
  poll: BarChart3,
  text: MessageSquare,
  story: Film,
  'article-share': Link2,
  'quote-tweet': MessageSquare,
};

/* Platform-tinted gradient swatches for placeholder images.
   Carousel tiles alternate across 4 variants for visual variety. */
const PLATFORM_GRADIENTS = {
  linkedin: ['from-blue-500/40 to-indigo-500/30', 'from-indigo-500/40 to-sky-500/25', 'from-slate-600/40 to-blue-500/30', 'from-blue-600/40 to-violet-500/25'],
  twitter: ['from-sky-500/40 to-cyan-500/25', 'from-slate-700/40 to-sky-500/25', 'from-sky-400/40 to-indigo-500/25', 'from-cyan-500/40 to-blue-500/25'],
  instagram: ['from-pink-500/40 to-orange-400/25', 'from-fuchsia-500/40 to-pink-500/25', 'from-rose-500/40 to-amber-400/25', 'from-purple-500/40 to-pink-500/25'],
  facebook: ['from-blue-600/40 to-indigo-600/25', 'from-indigo-600/40 to-blue-500/25', 'from-blue-500/40 to-slate-600/25', 'from-sky-600/40 to-blue-600/25'],
  tiktok: ['from-fuchsia-500/40 to-cyan-500/25', 'from-pink-500/40 to-teal-400/25', 'from-rose-500/40 to-cyan-400/25', 'from-fuchsia-500/40 to-sky-500/25'],
};

const MediaPlaceholder = ({ post, isDark, border, sectionBg, ts, tp }) => {
  const media = post.media;
  if (!media) return null;
  const gradients = PLATFORM_GRADIENTS[post.platform] || PLATFORM_GRADIENTS.linkedin;
  const overlayTint = isDark ? 'bg-white/[0.04]' : 'bg-white/[0.25]';
  const tileBase = `rounded-lg overflow-hidden border ${border} relative flex items-center justify-center`;

  // Carousel — horizontal strip of tiles
  if (media.type === 'carousel') {
    const count = media.slides || media.slideTitles?.length || 3;
    const titles = media.slideTitles || Array(count).fill('');
    return (
      <div className="flex gap-2 overflow-x-auto pb-1 mb-2 -mx-0.5 px-0.5" style={{ scrollbarWidth: 'thin' }}>
        {Array.from({ length: count }).map((_, i) => {
          const grad = gradients[i % gradients.length];
          return (
            <div key={i} className={`${tileBase} flex-shrink-0 w-[92px] h-[92px] bg-gradient-to-br ${grad}`}>
              <div className="absolute top-1.5 left-1.5 w-5 h-5 rounded-md bg-black/40 backdrop-blur-sm flex items-center justify-center">
                <span className="text-[9px] font-bold text-white">{i + 1}</span>
              </div>
              <LayoutGrid className={`w-5 h-5 ${isDark ? 'text-white/40' : 'text-white/70'}`} />
              {titles[i] && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent px-1.5 py-1">
                  <p className="text-[8px] leading-tight font-medium text-white line-clamp-2">{titles[i]}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Reel / Story — single 9:16 tall thumbnail with play icon
  if (media.type === 'reel' || media.type === 'story') {
    const grad = gradients[0];
    const isReel = media.type === 'reel';
    return (
      <div className="flex gap-3 items-start mb-2">
        <div className={`${tileBase} w-[120px] h-[213px] bg-gradient-to-br ${grad} flex-shrink-0`}>
          <div className={`w-11 h-11 rounded-full ${overlayTint} backdrop-blur-sm flex items-center justify-center`}>
            <Play className="w-5 h-5 text-white fill-white ml-0.5" />
          </div>
          <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-md bg-black/50 backdrop-blur-sm">
            <span className="text-[8px] font-medium text-white">{isReel ? (media.duration || '0:30') : 'Story'}</span>
          </div>
        </div>
        <div className="flex-1 min-w-0 pt-1">
          <p className={`text-[10px] uppercase tracking-wider ${ts} mb-0.5`}>{isReel ? 'Reel preview' : 'Story preview'}</p>
          <p className={`text-[11px] ${tp}`}>{media.duration && `Duration: ${media.duration}`}</p>
          {media.soundTrend && <p className={`text-[10px] ${ts} mt-0.5 italic`}>{media.soundTrend}</p>}
        </div>
      </div>
    );
  }

  // Article-share — 16:9 hero thumbnail
  if (media.type === 'article-share') {
    const grad = gradients[0];
    return (
      <div className={`${tileBase} w-full aspect-[16/9] bg-gradient-to-br ${grad} mb-2`}>
        <Link2 className={`w-8 h-8 ${isDark ? 'text-white/40' : 'text-white/70'}`} />
        <div className="absolute bottom-2 left-2 right-2">
          <p className="text-[10px] font-medium text-white/90 line-clamp-1">{media.linkPreview || 'Article preview'}</p>
        </div>
      </div>
    );
  }

  // Thread — 3 small stacked preview tiles (visual rhythm)
  if (media.type === 'thread') {
    return (
      <div className="flex gap-2 mb-2">
        {[0, 1, 2].map(i => (
          <div key={i} className={`${tileBase} flex-1 h-[60px] bg-gradient-to-br ${gradients[i % gradients.length]}`}>
            <MessageSquare className={`w-4 h-4 ${isDark ? 'text-white/40' : 'text-white/70'}`} />
            <div className="absolute top-1 left-1.5 text-[8px] font-bold text-white/80">{i + 1}/</div>
          </div>
        ))}
      </div>
    );
  }

  // Poll — colored banner with vote count
  if (media.type === 'poll') {
    return (
      <div className={`${tileBase} w-full h-[70px] bg-gradient-to-br ${gradients[0]} mb-2`}>
        <BarChart3 className={`w-6 h-6 ${isDark ? 'text-white/50' : 'text-white/80'}`} />
        {media.totalVotes && (
          <div className="absolute bottom-1.5 left-2 right-2 text-center">
            <p className="text-[10px] font-medium text-white/90">{media.totalVotes} votes · poll preview</p>
          </div>
        )}
      </div>
    );
  }

  // Quote-tweet — single square placeholder
  if (media.type === 'quote-tweet') {
    return (
      <div className={`${tileBase} w-full h-[80px] bg-gradient-to-br ${gradients[0]} mb-2`}>
        <MessageSquare className={`w-6 h-6 ${isDark ? 'text-white/40' : 'text-white/70'}`} />
        <div className="absolute bottom-1.5 left-2 right-2 text-center">
          <p className="text-[10px] font-medium text-white/90">Quote-tweet preview</p>
        </div>
      </div>
    );
  }

  // Text — subtle banner showing platform identity
  if (media.type === 'text') {
    return (
      <div className={`${tileBase} w-full h-[50px] bg-gradient-to-br ${gradients[0]} mb-2`}>
        <span className="text-[10px] font-medium text-white/90 uppercase tracking-wider">Text post</span>
      </div>
    );
  }

  return null;
};

const SocialDetailsPanel = ({ post, isDark, onClose, onApprove, approved }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const panelBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const hoverBg = isDark ? 'hover:bg-white/[0.05]' : 'hover:bg-black/[0.03]';
  const sectionBg = isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]';
  const [showThinking, setShowThinking] = useState(false);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!post) return null;
  const ps = PLATFORM_STYLES[post.platform];
  const status = POST_STATUS[post.status];
  const thumb = AGENT_THUMBS[post.agent];
  const MediaIcon = MEDIA_TYPE_ICONS[post.media?.type] || ImageIcon;
  const isDraft = post.status === 'draft' || post.needsReview;

  return (
    <>
      <div
        className={`fixed inset-0 z-40 ${isDark ? 'bg-black/40' : 'bg-black/20'} backdrop-blur-sm`}
        onClick={onClose}
        data-testid="social-details-backdrop"
      />
      <div
        className={`fixed top-0 right-0 z-50 h-full w-[480px] ${panelBg} border-l ${border} overflow-y-auto shadow-2xl`}
        style={{ animation: 'slideInRight 0.25s ease-out' }}
        data-testid={`social-details-panel-${post.id}`}
      >
        {/* Header */}
        <div className={`sticky top-0 ${panelBg} border-b ${border} px-5 py-4 flex items-start gap-3 z-10`}>
          {thumb && <img src={thumb} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5" />}
          <div className="flex-1 min-w-0">
            <p className={`text-[15px] font-semibold ${tp}`}>{post.title}</p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? ps.bg + ' ' + ps.color : ps.lightBg + ' ' + ps.lightColor}`}>{ps.label}</span>
              <span className={`text-[11px] ${ts}`}>{post.type}</span>
              <span className={`text-[10px] ${ts}`}>·</span>
              <span className={`text-[11px] ${ts}`}>{post.time}</span>
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${isDark ? status.dark : status.light}`}>{approved ? 'Approved' : status.label}</span>
            </div>
          </div>
          <button onClick={onClose} className={`flex-shrink-0 p-1.5 rounded-md ${ts} ${hoverBg}`} data-testid="social-details-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-5">
          {/* Caption */}
          <div>
            <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1.5 ${ts}`}>Caption</p>
            <div className={`rounded-lg border ${border} p-3 ${sectionBg}`}>
              <p className={`text-[12px] leading-relaxed whitespace-pre-line ${tp}`}>{post.caption || post.preview}</p>
            </div>
          </div>

          {/* Media */}
          {post.media && (
            <div>
              <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1.5 flex items-center gap-1 ${ts}`}>
                <MediaIcon className="w-3 h-3" /> Media — {post.media.type}
                {post.media.slides && <span className={`${ts} font-normal`}>· {post.media.slides} slides</span>}
                {post.media.duration && <span className={`${ts} font-normal`}>· {post.media.duration}</span>}
                {post.media.count && <span className={`${ts} font-normal`}>· {post.media.count} tweets</span>}
                {post.media.totalVotes && <span className={`${ts} font-normal`}>· {post.media.totalVotes} votes</span>}
              </p>
              <MediaPlaceholder post={post} isDark={isDark} border={border} sectionBg={sectionBg} ts={ts} tp={tp} />
              {post.media.slideTitles && (
                <div className={`rounded-lg border ${border} overflow-hidden`}>
                  {post.media.slideTitles.map((t, i) => (
                    <div key={i} className={`flex items-start gap-3 px-3 py-2 ${i % 2 === 0 ? sectionBg : ''} ${i !== 0 ? 'border-t ' + border : ''}`}>
                      <span className={`text-[10px] font-semibold w-5 ${ts}`}>{i + 1}.</span>
                      <span className={`text-[11px] flex-1 ${tp}`}>{t}</span>
                    </div>
                  ))}
                </div>
              )}
              {post.media.threadPreview && (
                <div className={`rounded-lg border ${border} overflow-hidden`}>
                  {post.media.threadPreview.map((t, i) => (
                    <div key={i} className={`flex items-start gap-3 px-3 py-2 ${i % 2 === 0 ? sectionBg : ''} ${i !== 0 ? 'border-t ' + border : ''}`}>
                      <span className={`text-[10px] font-semibold w-5 ${ts}`}>{i + 1}/</span>
                      <span className={`text-[11px] flex-1 ${tp}`}>{t}</span>
                    </div>
                  ))}
                </div>
              )}
              {post.media.options && (
                <div className={`rounded-lg border ${border} overflow-hidden`}>
                  {post.media.options.map((opt, i) => (
                    <div key={i} className={`px-3 py-2 text-[11px] ${tp} ${i % 2 === 0 ? sectionBg : ''} ${i !== 0 ? 'border-t ' + border : ''}`}>
                      {opt}
                    </div>
                  ))}
                </div>
              )}
              {post.media.hook && (
                <div className={`rounded-lg border ${border} px-3 py-2 ${sectionBg}`}>
                  <p className={`text-[10px] uppercase tracking-wider ${ts}`}>Hook</p>
                  <p className={`text-[11px] mt-1 ${tp}`}>{post.media.hook}</p>
                  {post.media.soundTrend && <p className={`text-[10px] mt-1.5 ${ts}`}>{post.media.soundTrend}</p>}
                </div>
              )}
              {post.media.linkPreview && (
                <div className={`rounded-lg border ${border} px-3 py-2 ${sectionBg}`}>
                  <p className={`text-[10px] flex items-center gap-1 ${ts}`}><Link2 className="w-3 h-3" /> Link preview</p>
                  <p className={`text-[11px] mt-1 ${tp}`}>{post.media.linkPreview}</p>
                  <p className={`text-[10px] mt-0.5 ${ts}`}>{post.media.articleUrl}</p>
                </div>
              )}
              {post.media.quotedTweet && (
                <div className={`rounded-lg border ${border} px-3 py-2 ${sectionBg}`}>
                  <p className={`text-[11px] italic ${tp}`}>"{post.media.quotedTweet}"</p>
                  <p className={`text-[10px] mt-1 ${ts}`}>— {post.media.quotedAuthor}</p>
                </div>
              )}
            </div>
          )}

          {/* Hashtags + Mentions */}
          {(post.hashtags?.length > 0 || post.mentions?.length > 0) && (
            <div className="space-y-2">
              {post.hashtags?.length > 0 && (
                <div>
                  <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1 flex items-center gap-1 ${ts}`}><Hash className="w-3 h-3" /> Hashtags</p>
                  <div className="flex flex-wrap gap-1.5">
                    {post.hashtags.map((h, i) => (
                      <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full ${isDark ? 'bg-indigo-500/10 text-indigo-400' : 'bg-indigo-500/10 text-indigo-600'}`}>{h}</span>
                    ))}
                  </div>
                </div>
              )}
              {post.mentions?.length > 0 && (
                <div>
                  <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1 flex items-center gap-1 ${ts}`}><AtSign className="w-3 h-3" /> Mentions</p>
                  <div className="flex flex-wrap gap-1.5">
                    {post.mentions.map((m, i) => (
                      <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full ${isDark ? 'bg-white/[0.06] text-white/70' : 'bg-black/[0.06] text-slate-700'}`}>{m}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Platform meta (character count, audience, etc.) */}
          {post.platformMeta && (
            <div>
              <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1.5 ${ts}`}>Platform details</p>
              <div className={`rounded-lg border ${border} overflow-hidden`}>
                {Object.entries(post.platformMeta).map(([k, v], i) => (
                  <div key={k} className={`flex items-start justify-between gap-3 px-3 py-2 ${i % 2 === 0 ? sectionBg : ''} ${i !== 0 ? 'border-t ' + border : ''}`}>
                    <span className={`text-[10px] ${ts}`}>{k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</span>
                    <span className={`text-[10px] font-medium text-right ${tp}`}>{typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Engagement (published only) */}
          {post.engagement && (
            <div>
              <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1.5 ${ts}`}>Engagement</p>
              <div className={`grid grid-cols-3 gap-2 rounded-lg border ${border} overflow-hidden`}>
                <div className={`px-3 py-2 ${sectionBg}`}>
                  <p className={`text-[10px] flex items-center gap-1 ${ts}`}><Heart className="w-3 h-3" /> Likes</p>
                  <p className={`text-[14px] font-semibold ${tp} mt-0.5`}>{post.engagement.likes}</p>
                </div>
                <div className="px-3 py-2">
                  <p className={`text-[10px] flex items-center gap-1 ${ts}`}><MessageSquare className="w-3 h-3" /> Comments</p>
                  <p className={`text-[14px] font-semibold ${tp} mt-0.5`}>{post.engagement.comments}</p>
                </div>
                <div className={`px-3 py-2 ${sectionBg}`}>
                  <p className={`text-[10px] flex items-center gap-1 ${ts}`}><Repeat className="w-3 h-3" /> Shares</p>
                  <p className={`text-[14px] font-semibold ${tp} mt-0.5`}>{post.engagement.shares}</p>
                </div>
              </div>
            </div>
          )}

          {/* Thinking trail */}
          {post.thinking && (
            <div>
              <button onClick={() => setShowThinking(!showThinking)} className={`flex items-center gap-1.5 text-[10px] font-medium ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} data-testid={`social-thinking-${post.id}`}>
                <Brain className="w-3 h-3" /> {showThinking ? 'Hide Buzz\'s Thinking' : 'Show Buzz\'s Thinking'}
              </button>
              {showThinking && (
                <div className={`mt-2 px-3 py-2.5 rounded-lg text-[11px] leading-relaxed ${isDark ? 'bg-indigo-500/5 border border-indigo-500/10 text-indigo-300' : 'bg-indigo-500/5 border border-indigo-500/10 text-indigo-700'}`}>
                  {post.thinking}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          {isDraft && !approved && (
            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={() => { onApprove?.(post.id); onClose(); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium transition-colors ${isDark ? 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25' : 'bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/25'}`}
                data-testid={`social-details-approve-${post.id}`}
              >
                <CheckCircle className="w-3.5 h-3.5" /> Approve & Schedule
              </button>
              <button className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium border ${border} ${ts} ${hoverBg}`}>
                Edit caption
              </button>
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes slideInRight { from { transform: translateX(20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
    </>
  );
};

/* ── Activity Item ── */
const ActivityItem = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const thumb = AGENT_THUMBS[item.agent];
  const dotColor = item.type === 'publish' ? 'bg-emerald-400' : item.type === 'insight' ? 'bg-indigo-400' : 'bg-blue-400';

  return (
    <div className="flex items-start gap-2 py-1.5">
      {thumb && <img src={thumb} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0 mt-0.5" />}
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] leading-relaxed ${tp}`}><span className="font-semibold">{AGENT_NAMES[item.agent]}</span> {item.action}</p>
        <p className={`text-[9px] mt-0.5 ${ts}`}>{item.time}</p>
      </div>
      <div className={`w-1.5 h-1.5 rounded-full ${dotColor} flex-shrink-0 mt-1.5`} />
    </div>
  );
};

/* ── Chat Message ── */
const ChatMessage = ({ msg, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  if (msg.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-sm leading-relaxed ${isDark ? 'bg-indigo-500/15 text-white' : 'bg-indigo-500/10 text-slate-800'}`}>{msg.text}</div>
      </div>
    );
  }
  return (
    <div className="flex gap-3">
      <img src={AGENT_THUMBS.buzz} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />
      <div className={`text-sm leading-relaxed whitespace-pre-line ${tp}`}>{msg.text}</div>
    </div>
  );
};

/* ════════════════════════════════════════ */
/* ── Main Component ── */
/* ════════════════════════════════════════ */

const V4MktSocial = ({ isDark, onWorkflowToggle, workflowPanelOpen, onNavigate, activeUser, priyaState, socialAdHocScenario, onSocialAdHocDone }) => {
  const [viewMode, setViewMode] = useState('weekly'); // weekly | daily | monthly
  const [selectedDate, setSelectedDate] = useState(TODAY);
  const [expandedDate, setExpandedDate] = useState(TODAY);
  const [approvedPosts, setApprovedPosts] = useState(new Set());
  const [message, setMessage] = useState('');
  useRegisterChatInput(setMessage);
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [detailsPostId, setDetailsPostId] = useState(null);
  const [deepDivePostId, setDeepDivePostId] = useState(null);
  const [channelDeepDiveId, setChannelDeepDiveId] = useState(null);
  const [newPostFlow, setNewPostFlow] = useState(false);
  const messagesEndRef = useRef(null);

  const isPriya = activeUser?.id === 'marketing';

  const data = SOCIAL_DATA;
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';
  const cellBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';
  const cellActiveBg = isDark ? 'bg-indigo-500/10 border-indigo-500/20' : 'bg-indigo-500/8 border-indigo-500/20';

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  // Merge enriched base posts with any Priya-created posts (pinned to their schedule date)
  const getPostsForDate = useCallback((dk) => {
    const base = baseGetPostsForDate(dk);
    const created = priyaState?.createdPosts?.[dk] || [];
    return [...created, ...base];
  }, [priyaState?.createdPosts]);

  const selectedDateObj = useMemo(() => new Date(selectedDate + 'T00:00:00'), [selectedDate]);

  // Week days for weekly view
  const weekDays = useMemo(() => {
    const ws = getWeekStart(selectedDateObj);
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDays(ws, i);
      const dk = dateKey(d);
      return { date: d, key: dk, posts: getPostsForDate(dk) };
    });
  }, [selectedDateObj, getPostsForDate]);

  // Month grid for monthly view
  const monthGrid = useMemo(() => {
    const year = selectedDateObj.getFullYear();
    const month = selectedDateObj.getMonth();
    const first = new Date(year, month, 1);
    const startDay = first.getDay(); // 0=Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    // Leading blanks
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dt = new Date(year, month, d);
      const dk = dateKey(dt);
      cells.push({ date: dt, key: dk, day: d, posts: getPostsForDate(dk) });
    }
    return cells;
  }, [selectedDateObj, getPostsForDate]);

  const navigateWeek = (dir) => {
    const d = addDays(selectedDateObj, dir * 7);
    setSelectedDate(dateKey(d));
    setExpandedDate(null);
  };

  const navigateMonth = (dir) => {
    const d = new Date(selectedDateObj);
    d.setMonth(d.getMonth() + dir);
    setSelectedDate(dateKey(d));
    setExpandedDate(null);
  };

  const navigateDay = (dir) => {
    const d = addDays(selectedDateObj, dir);
    setSelectedDate(dateKey(d));
  };

  const handleDayClick = (dk) => {
    setExpandedDate(expandedDate === dk ? null : dk);
  };

  const sendMessage = (text, responseData) => {
    const userMsg = { role: 'user', text };
    const agentMsg = { role: 'agent', text: responseData?.text || SOCIAL_CHAT_RESPONSES.general.text };
    setChatMessages(prev => [...prev, userMsg, agentMsg]);
    setChatMode(true);
  };

  const handleCreatePost = () => {
    if (isPriya) {
      setNewPostFlow(true);
      return;
    }
    sendMessage('I want to create a new social post.', SOCIAL_CHAT_RESPONSES.create);
  };

  const handleBackFromNewPost = () => {
    const scenario = PRIYA_SOCIAL_NEW_POST['new-post'];
    if (scenario?.createdPost && priyaState?.createPriyaPost && scenario.scheduleDateDefault) {
      priyaState.createPriyaPost(scenario.createdPost, scenario.scheduleDateDefault);
      // Jump the calendar to the day the new post was pinned
      setSelectedDate(scenario.scheduleDateDefault);
      setExpandedDate(scenario.scheduleDateDefault);
    }
    setNewPostFlow(false);
  };

  const handleSend = () => { if (message.trim()) { sendMessage(message.trim(), null); setMessage(''); } };

  const handleInput = (e) => {
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    setMessage(e.target.value);
  };

  /* ── Expanded detail panel for a date ── */
  const renderExpandedDay = (dk) => {
    const posts = getPostsForDate(dk);
    if (!posts.length) return <p className={`text-xs py-4 text-center ${ts}`}>No posts scheduled</p>;

    const approved = posts.filter(p => (p.status === 'published' || p.status === 'scheduled') && !p.needsReview);
    const needsAction = posts.filter(p => p.needsReview || p.status === 'draft');

    return (
      <div className="space-y-3 py-3" data-testid={`social-expanded-${dk}`}>
        {approved.length > 0 && (
          <div>
            <p className={`text-[10px] font-semibold mb-2 flex items-center gap-1.5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
              <CheckCircle className="w-3 h-3" /> Approved & Scheduled ({approved.length})
            </p>
            <div className="flex flex-col gap-1.5">
              {approved.map(p => <PostRow key={p.id} post={p} isDark={isDark} onApprove={() => {}} onDetails={setDetailsPostId} onDeepDive={setDeepDivePostId} approved={false} />)}
            </div>
          </div>
        )}
        {needsAction.length > 0 && (
          <div>
            <p className={`text-[10px] font-semibold mb-2 flex items-center gap-1.5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
              <Eye className="w-3 h-3" /> Needs Review / In Progress ({needsAction.length})
            </p>
            <div className="flex flex-col gap-1.5">
              {needsAction.map(p => (
                <PostRow
                  key={p.id}
                  post={p}
                  isDark={isDark}
                  onApprove={(id) => setApprovedPosts(prev => new Set([...prev, id]))}
                  onDetails={setDetailsPostId}
                  onDeepDive={setDeepDivePostId}
                  approved={approvedPosts.has(p.id)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  /* ── Chat input ── */
  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea value={message} onChange={handleInput} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Message Buzz about social..." rows={1}
              className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`}
              style={{ height: 'auto', maxHeight: '200px', overflow: message.split('\n').length > 8 ? 'auto' : 'hidden' }}
              data-testid="social-chat-input"
            />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
                <img src={AGENT_THUMBS.buzz} alt="" className="w-5 h-5 rounded-full object-cover" />
                <span className={`text-xs font-medium ${tp}`}>Buzz</span>
              </div>
              <button onClick={onWorkflowToggle} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`} data-testid="social-workflow-btn">
                <Zap className={`w-3.5 h-3.5 ${tp}`} />
                <span className={`text-xs font-medium ${tp}`}>Workflows</span>
              </button>
              <button className={`p-1.5 rounded-lg transition-colors ${hoverBg}`}><Paperclip className={`w-3.5 h-3.5 ${ts}`} /></button>
            </div>
            <div className="flex items-center gap-1.5">
              <button className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${hoverBg}`}><Mic className={`w-4 h-4 ${ts}`} /></button>
              <button onClick={handleSend} className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`} data-testid="social-send-btn">
                <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  /* ── Channel Deep Dive (Priya-only) ── */
  if (channelDeepDiveId && isPriya) {
    const ddScenario = PRIYA_SOCIAL_CHANNEL_DD[channelDeepDiveId];
    if (ddScenario) {
      return (
        <V4AdHocChat
          isDark={isDark}
          scenarioId={ddScenario.id}
          scenarios={{ [ddScenario.id]: ddScenario }}
          backLabel="Back to Social"
          onBack={() => setChannelDeepDiveId(null)}
        />
      );
    }
  }

  /* ── Deep Dive per post ── */
  if (deepDivePostId) {
    // Find post across all dates
    const allDates = new Set([
      ...Object.keys(SOCIAL_DATA.postsByDate || {}),
      ...Object.keys(priyaState?.createdPosts || {}),
    ]);
    let ddPost = null;
    for (const dk of allDates) {
      const p = getPostsForDate(dk).find(x => x.id === deepDivePostId);
      if (p) { ddPost = p; break; }
    }
    const ddScenario = ddPost ? buildSocialDeepDive(ddPost) : null;
    if (ddScenario) {
      return (
        <V4AdHocChat
          isDark={isDark}
          scenarioId={ddScenario.id}
          scenarios={{ [ddScenario.id]: ddScenario }}
          backLabel="Back to Social"
          onBack={() => setDeepDivePostId(null)}
        />
      );
    }
  }

  /* ── New Post flow (Priya only) ── */
  if (newPostFlow && isPriya) {
    return (
      <V4AdHocChat
        isDark={isDark}
        scenarioId="new-post"
        scenarios={PRIYA_SOCIAL_NEW_POST}
        backLabel="Back to Social"
        onBack={handleBackFromNewPost}
      />
    );
  }

  /* ── Keypress A/B scenarios (Priya only, driven by wrapper) ── */
  if (socialAdHocScenario && isPriya) {
    return (
      <V4AdHocChat
        isDark={isDark}
        scenarioId={socialAdHocScenario}
        scenarios={PRIYA_SOCIAL_KEYPRESS}
        backLabel="Back to Social"
        onBack={() => onSocialAdHocDone?.()}
        skipFirstUserDelay
      />
    );
  }

  /* ── Chat view ── */
  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid="social-chat-view">
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button onClick={() => setChatMode(false)} className={`flex items-center gap-2 px-2 py-1 rounded-lg transition-colors ${hoverBg}`} data-testid="social-back-btn">
            <ArrowLeft className={`w-4 h-4 ${ts}`} />
            <span className={`text-sm font-medium ${tp}`}>Back to Social</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="flex justify-center"><div className="w-[80%] py-5 space-y-5">
            {chatMessages.map((msg, i) => <ChatMessage key={i} msg={msg} isDark={isDark} />)}
            <div ref={messagesEndRef} />
          </div></div>
        </div>
        {chatInput}
      </div>
    );
  }

  /* ── View toggle + nav header ── */
  const viewToggle = (
    <div className="flex items-center gap-1.5">
      {['weekly', 'daily', 'monthly'].map(v => (
        <button key={v} onClick={() => { setViewMode(v); setExpandedDate(null); }}
          className={`px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-colors ${viewMode === v
            ? (isDark ? 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20')
            : `${border} ${ts} ${hoverBg}`}`}
          data-testid={`social-view-${v}`}
        >
          {v.charAt(0).toUpperCase() + v.slice(1)}
        </button>
      ))}
    </div>
  );

  const navArrows = (
    <div className="flex items-center gap-2">
      <button onClick={() => viewMode === 'monthly' ? navigateMonth(-1) : viewMode === 'daily' ? navigateDay(-1) : navigateWeek(-1)}
        className={`p-1 rounded-md ${hoverBg}`} data-testid="social-nav-prev"><ChevronLeft className={`w-4 h-4 ${ts}`} /></button>
      <span className={`text-xs font-medium ${tp}`}>
        {viewMode === 'monthly' && `${MONTH_NAMES[selectedDateObj.getMonth()]} ${selectedDateObj.getFullYear()}`}
        {viewMode === 'weekly' && `${MONTH_NAMES[weekDays[0].date.getMonth()]} ${weekDays[0].date.getDate()} – ${weekDays[6].date.getDate()}, ${weekDays[6].date.getFullYear()}`}
        {viewMode === 'daily' && `${DAY_NAMES[selectedDateObj.getDay()]}, ${MONTH_NAMES[selectedDateObj.getMonth()]} ${selectedDateObj.getDate()}`}
      </span>
      <button onClick={() => viewMode === 'monthly' ? navigateMonth(1) : viewMode === 'daily' ? navigateDay(1) : navigateWeek(1)}
        className={`p-1 rounded-md ${hoverBg}`} data-testid="social-nav-next"><ChevronRight className={`w-4 h-4 ${ts}`} /></button>
      <button onClick={() => { setSelectedDate(TODAY); setExpandedDate(null); }}
        className={`px-2 py-0.5 rounded-md text-[10px] font-medium border ${border} ${ts} ${hoverBg}`} data-testid="social-today-btn">Today</button>
    </div>
  );

  /* ── Data view ── */
  return (
    <div className="flex flex-col h-full" data-testid="social-page">
      {/* Header */}
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Calendar className={`w-5 h-5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
            <p className={`text-base font-semibold ${tp}`}>Social</p>
          </div>
          <div className="flex items-center gap-2">
            {viewToggle}
            <button onClick={handleCreatePost} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:opacity-90" data-testid="social-create-btn">
              <Plus className="w-3.5 h-3.5" /> New Post
            </button>
          </div>
        </div>
        <div className="flex gap-2">
          {data.platforms.map(p => <PlatformCard key={p.id} platform={p} isDark={isDark} onViewReport={(channelId) => onNavigate?.('mkt-analytics', { channel: channelId })} onChannelDeepDive={isPriya ? setChannelDeepDiveId : null} />)}
          {/* Connect more */}
          <button
            onClick={() => onNavigate?.('integrations', { filter: 'social' })}
            className={`flex flex-col items-center justify-center min-w-[80px] rounded-xl border border-dashed ${border} ${hoverBg} transition-colors px-3 py-2.5`}
            data-testid="social-connect-more"
          >
            <Plus className={`w-5 h-5 mb-1 ${ts}`} />
            <span className={`text-[9px] font-medium ${ts}`}>Connect</span>
          </button>
        </div>
      </div>

      <div className={`border-t ${border}`} />

      {/* Main: Calendar + Activity */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="grid grid-cols-4 gap-5 h-full px-5 py-4">
          {/* Calendar area (3 cols) */}
          <div className="col-span-3 flex flex-col min-h-0 overflow-y-auto pr-1">
            {/* Nav arrows */}
            <div className="flex items-center justify-between mb-3">
              {navArrows}
            </div>

            {/* ── WEEKLY VIEW ── */}
            {viewMode === 'weekly' && (
              <div className="space-y-0">
                <div className="grid grid-cols-7 gap-2">
                  {weekDays.map(({ date, key: dk, posts }) => {
                    const isToday = dk === TODAY;
                    const isExpanded = expandedDate === dk;
                    const published = posts.filter(p => p.status === 'published').length;
                    const scheduled = posts.filter(p => p.status === 'scheduled').length;
                    const draft = posts.filter(p => p.status === 'draft').length;
                    const hasPosts = posts.length > 0;

                    return (
                      <button key={dk} onClick={() => handleDayClick(dk)}
                        className={`rounded-xl px-2 py-3 text-center border transition-all ${isExpanded ? cellActiveBg : `${border} ${hasPosts ? cellBg : ''}`} ${isToday ? (isDark ? 'ring-1 ring-indigo-500/30' : 'ring-1 ring-indigo-500/30') : ''}`}
                        data-testid={`social-week-day-${dk}`}
                      >
                        <p className={`text-[10px] ${ts}`}>{DAY_NAMES[date.getDay()]}</p>
                        <p className={`text-lg font-bold ${isToday ? (isDark ? 'text-indigo-400' : 'text-indigo-600') : tp}`}>{date.getDate()}</p>
                        {hasPosts ? (
                          <div className="flex items-center justify-center gap-1 mt-1.5">
                            {published > 0 && <div className="flex items-center gap-0.5"><div className="w-1.5 h-1.5 rounded-full bg-emerald-400" /><span className={`text-[9px] ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{published}</span></div>}
                            {scheduled > 0 && <div className="flex items-center gap-0.5"><div className="w-1.5 h-1.5 rounded-full bg-indigo-400" /><span className={`text-[9px] ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>{scheduled}</span></div>}
                            {draft > 0 && <div className="flex items-center gap-0.5"><div className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-white/20' : 'bg-slate-300'}`} /><span className={`text-[9px] ${ts}`}>{draft}</span></div>}
                          </div>
                        ) : (
                          <p className={`text-[9px] mt-1.5 ${ts}`}>—</p>
                        )}
                        {/* Platform dots */}
                        {hasPosts && (
                          <div className="flex items-center justify-center gap-1 mt-1">
                            {[...new Set(posts.map(p => p.platform))].map(pl => (
                              <div key={pl} className={`w-2 h-2 rounded-full ${PLATFORM_STYLES[pl].dot}`} title={PLATFORM_STYLES[pl].label} />
                            ))}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                {/* Expanded detail below calendar */}
                {expandedDate && (
                  <div className={`mt-3 px-2 rounded-xl border ${border} ${isDark ? 'bg-[#0a1220]' : 'bg-[#ede7de]'}`}>
                    <div className="flex items-center justify-between pt-3 px-1">
                      <p className={`text-xs font-semibold ${tp}`}>
                        {(() => { const d = new Date(expandedDate + 'T00:00:00'); return `${DAY_NAMES[d.getDay()]}, ${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`; })()}
                      </p>
                      <button onClick={() => setExpandedDate(null)} className={`text-[10px] ${ts} ${hoverBg} px-2 py-0.5 rounded-md`}>
                        <ChevronUp className="w-3 h-3 inline" /> Collapse
                      </button>
                    </div>
                    {renderExpandedDay(expandedDate)}
                  </div>
                )}
              </div>
            )}

            {/* ── DAILY VIEW ── */}
            {viewMode === 'daily' && (
              <div>
                {renderExpandedDay(selectedDate)}
                {getPostsForDate(selectedDate).length === 0 && (
                  <div className={`text-center py-12 ${ts}`}>
                    <Calendar className={`w-8 h-8 mx-auto mb-2 ${ts}`} />
                    <p className="text-sm">No posts scheduled for this day</p>
                  </div>
                )}
              </div>
            )}

            {/* ── MONTHLY VIEW ── */}
            {viewMode === 'monthly' && (
              <div className="space-y-0">
                {/* Day headers */}
                <div className="grid grid-cols-7 gap-1 mb-1">
                  {DAY_NAMES.map(d => <p key={d} className={`text-[10px] text-center font-medium py-1 ${ts}`}>{d}</p>)}
                </div>
                {/* Calendar cells */}
                <div className="grid grid-cols-7 gap-1">
                  {monthGrid.map((cell, i) => {
                    if (!cell) return <div key={`blank-${i}`} />;
                    const isToday = cell.key === TODAY;
                    const isExpanded = expandedDate === cell.key;
                    const hasPosts = cell.posts.length > 0;

                    return (
                      <button key={cell.key} onClick={() => hasPosts ? handleDayClick(cell.key) : null}
                        className={`rounded-lg p-1.5 text-center border transition-all min-h-[56px] ${isExpanded ? cellActiveBg : `${border} ${hasPosts ? cellBg : ''}`} ${isToday ? (isDark ? 'ring-1 ring-indigo-500/30' : 'ring-1 ring-indigo-500/30') : ''} ${hasPosts ? 'cursor-pointer' : 'cursor-default'}`}
                        data-testid={`social-month-day-${cell.key}`}
                      >
                        <p className={`text-[11px] font-medium ${isToday ? (isDark ? 'text-indigo-400' : 'text-indigo-600') : tp}`}>{cell.day}</p>
                        {hasPosts && (
                          <div className="flex items-center justify-center gap-0.5 mt-1">
                            {[...new Set(cell.posts.map(p => p.platform))].map(pl => (
                              <div key={pl} className={`w-1.5 h-1.5 rounded-full ${PLATFORM_STYLES[pl].dot}`} />
                            ))}
                            <span className={`text-[8px] ml-0.5 ${ts}`}>{cell.posts.length}</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                {/* Expanded detail below calendar */}
                {expandedDate && (
                  <div className={`mt-3 px-2 rounded-xl border ${border} ${isDark ? 'bg-[#0a1220]' : 'bg-[#ede7de]'}`}>
                    <div className="flex items-center justify-between pt-3 px-1">
                      <p className={`text-xs font-semibold ${tp}`}>
                        {(() => { const d = new Date(expandedDate + 'T00:00:00'); return `${DAY_NAMES[d.getDay()]}, ${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`; })()}
                      </p>
                      <button onClick={() => setExpandedDate(null)} className={`text-[10px] ${ts} ${hoverBg} px-2 py-0.5 rounded-md`}>
                        <ChevronUp className="w-3 h-3 inline" /> Collapse
                      </button>
                    </div>
                    {renderExpandedDay(expandedDate)}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Activity sidebar (1 col) */}
          <div className="col-span-1 flex flex-col min-h-0 overflow-y-auto pl-1">
            <div className="flex items-center gap-2 mb-3">
              <Zap className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Buzz Activity</p>
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-indigo-400/10 text-indigo-400' : 'bg-indigo-600/10 text-indigo-600'}`}>Live</span>
            </div>
            <div className={`flex flex-col divide-y ${isDark ? 'divide-white/[0.04]' : 'divide-black/[0.04]'}`}>
              {data.recentActivity.map(item => <ActivityItem key={item.id} item={item} isDark={isDark} />)}
            </div>
          </div>
        </div>
      </div>

      {chatInput}

      {/* Post details drawer */}
      {detailsPostId && (() => {
        // Find post across all merged dates (base + created)
        const allDates = new Set([
          ...Object.keys(SOCIAL_DATA.postsByDate || {}),
          ...Object.keys(priyaState?.createdPosts || {}),
        ]);
        let found = null;
        for (const dk of allDates) {
          const p = getPostsForDate(dk).find(x => x.id === detailsPostId);
          if (p) { found = p; break; }
        }
        if (!found) return null;
        return (
          <SocialDetailsPanel
            post={found}
            isDark={isDark}
            onClose={() => setDetailsPostId(null)}
            onApprove={(id) => setApprovedPosts(prev => new Set([...prev, id]))}
            approved={approvedPosts.has(found.id)}
          />
        );
      })()}
    </div>
  );
};

export default V4MktSocial;
